'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Wordmark } from './brand';
import { EmailLink } from './email-link';
import { IndustryIcon } from './industry-icon';
import { NavIcon } from './nav-icon';
import { DfwMap } from './dfw-map';
import { CITIES } from '@/lib/locations';
import { trackCta } from '@/lib/attribution';
import type { PricingCategory } from '@/lib/pricing-nav';
import type { MegaMenu, MenuFeature, TopItem } from '@/lib/nav-menu';

/**
 * Header with full-width menus.
 *
 * Each top item opens one panel that spans the page under the bar: a large
 * title link on the left, then grouped columns. Only one panel is open at a
 * time. A mouse opens it on hover (after a short pause, so passing over the bar
 * does not flash panels); a click, Enter or Space toggles it; Escape closes it
 * and returns focus to its button; moving focus or the pointer away closes it.
 * Each menu is a disclosure (button + links), not an ARIA menu, so Tab simply
 * walks the links. Below 1024px the same groups live in a full-screen sheet.
 */

const Chevron = ({ open }: { open: boolean }) => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ArrowBox = () => (
  <span aria-hidden="true" className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-spark-600 text-white transition-transform duration-200 group-hover:translate-x-1">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 6 6 6-6 6" />
    </svg>
  </span>
);

/** Industries: icon tiles on the left, the Dallas–Fort Worth map card on the right. */
function IndustriesBody({ menu, pathname }: { menu: MegaMenu; pathname: string }) {
  const [who] = menu.columns;
  return (
    <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1fr_19rem]">
      <div>
        <p className="text-sm text-mist-500">{who.heading}</p>
        <ul className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3">
          {who.links.map((l) => (
            <li key={l.href}>
              <Link
                href={l.href}
                aria-current={pathname === l.href ? 'page' : undefined}
                className="press group/tile flex h-full min-h-[3.5rem] items-center gap-3 rounded-xl border border-line px-3 py-2.5 text-[0.95rem] font-medium text-mist-100 transition-colors hover:border-[#09090b] hover:bg-[#09090b] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-spark-500"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-spark-500/10 text-spark-600 transition-colors group-hover/tile:bg-signal group-hover/tile:text-on-signal">
                  <IndustryIcon slug={l.icon ?? ''} className="h-[1.15rem] w-[1.15rem]" />
                </span>
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <Link
        href="/locations"
        className="press group/map relative flex flex-col overflow-hidden rounded-2xl bg-[#09090b] p-5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spark-500"
      >
        <span className="text-sm text-white/60">Where we work</span>
        <span className="mt-1 font-display text-2xl font-bold tracking-[-0.03em]">Dallas–Fort Worth, TX</span>
        <DfwMap className="my-1 h-36 w-full text-white" />
        <span className="flex flex-wrap gap-1.5">
          {CITIES.filter((c) => c.priority <= 2).map((c) => (
            <span key={c.slug} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${c.priority === 1 ? 'bg-signal text-on-signal' : 'bg-white/10 text-white/80'}`}>
              {c.name}
            </span>
          ))}
        </span>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-signal">
          See all {CITIES.length} cities
          <span aria-hidden="true" className="transition-transform group-hover/map:translate-x-1">→</span>
        </span>
      </Link>
    </div>
  );
}

/** The card at the right of a panel: one thing worth doing next. */
function FeatureCard({ f }: { f: MenuFeature }) {
  const dark = f.tone === 'dark';
  return (
    <Link
      href={f.href}
      className={`press group/feat relative flex flex-col self-start overflow-hidden rounded-2xl p-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-spark-500 ${dark ? 'bg-[#09090b] text-white' : 'bg-signal text-on-signal'}`}
    >
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${dark ? 'bg-signal text-on-signal' : 'bg-[#09090b] text-signal'}`}>
        <NavIcon name={f.icon} className="h-5 w-5" />
      </span>
      <span className={`mt-5 text-sm ${dark ? 'text-white/60' : 'text-on-signal/70'}`}>{f.eyebrow}</span>
      <span className="mt-1 font-display text-2xl font-bold leading-tight tracking-[-0.03em]">{f.title}</span>
      <span className={`mt-2 text-sm ${dark ? 'text-white/70' : ''}`}>{f.body}</span>
      <span className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${dark ? 'text-signal' : ''}`}>
        {f.cta}
        <span aria-hidden="true" className="transition-transform group-hover/feat:translate-x-1">→</span>
      </span>
    </Link>
  );
}

/** Services, Resources, Pricing: the main choices as icon tiles, the rest as short icon links, and a feature card. */
function TilesBody({ menu, pathname }: { menu: MegaMenu; pathname: string }) {
  const [main, ...rest] = menu.columns;
  return (
    <div className={`grid grid-cols-1 gap-8 ${menu.feature ? 'xl:grid-cols-[1fr_17rem]' : ''}`}>
      <div>
        <p className="text-sm text-mist-500">{main.heading}</p>
        <ul className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3">
          {main.links.map((l) => (
            <li key={l.href + l.label}>
              <Link
                href={l.href}
                aria-current={pathname === l.href ? 'page' : undefined}
                className="press group/tile flex h-full flex-col rounded-xl border border-line p-4 transition-colors hover:border-[#09090b] hover:bg-[#09090b] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-spark-500"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-spark-500/10 text-spark-600 transition-colors group-hover/tile:bg-signal group-hover/tile:text-on-signal">
                  <NavIcon name={l.icon} className="h-5 w-5" />
                </span>
                <span className="mt-3 font-semibold text-mist-100 group-hover/tile:text-white">{l.label}</span>
                {l.desc && <span className="mt-1 text-[0.82rem] leading-snug text-mist-400 group-hover/tile:text-white/70">{l.desc}</span>}
                {l.note && <span className="mt-auto pt-2 text-[0.82rem] font-semibold text-spark-500 group-hover/tile:text-signal">{l.note}</span>}
              </Link>
            </li>
          ))}
        </ul>
        {rest.length > 0 && (
          <div className="mt-7 grid grid-cols-1 gap-x-8 gap-y-6 border-t border-line pt-6 md:grid-cols-2">
            {rest.map((col) => (
              <div key={col.heading}>
                <p className="text-sm text-mist-500">{col.heading}</p>
                <ul className="mt-2 grid grid-cols-1 gap-0.5">
                  {col.links.map((l) => (
                    <li key={l.href + l.label}>
                      <Link
                        href={l.href}
                        aria-current={pathname === l.href ? 'page' : undefined}
                        className="press group/link -mx-2 flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-veil-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-spark-500"
                      >
                        <NavIcon name={l.icon} className="h-4 w-4 shrink-0 text-spark-600" />
                        <span className="text-[0.95rem] font-medium text-mist-100 decoration-spark-500 decoration-2 underline-offset-4 group-hover/link:underline">{l.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
      {menu.feature && <FeatureCard f={menu.feature} />}
    </div>
  );
}

function Panel({ menu, id, pathname, hidden }: { menu: MegaMenu; id: string; pathname: string; hidden: boolean }) {
  return (
    <div id={id} hidden={hidden} className="absolute inset-x-0 top-full border-b border-line-2 bg-ink-900 shadow-[0_30px_60px_-30px_rgb(0_0_0/0.35)]">
      <div className="container-page grid grid-cols-1 gap-10 py-10 lg:grid-cols-[minmax(0,15rem)_1fr] xl:py-12">
        <div>
          <Link href={menu.titleHref} className="group inline-flex items-center gap-3 font-display text-3xl font-semibold tracking-[-0.03em] text-mist-100">
            {menu.title}
            <ArrowBox />
          </Link>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-mist-400">{menu.intro}</p>
        </div>
        {menu.layout === 'industries' ? <IndustriesBody menu={menu} pathname={pathname} /> : <TilesBody menu={menu} pathname={pathname} />}
      </div>
    </div>
  );
}

export function SiteHeader({ salesEmail, nav: TOP_NAV }: { salesEmail: string; nav: TopItem[]; pricing?: PricingCategory[] }) {
  const [scrolled, setScrolled] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const pathname = usePathname();
  const headerRef = useRef<HTMLElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Navigation closes everything.
  useEffect(() => {
    setSheet(false);
    setOpenId(null);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = sheet ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sheet]);

  const close = useCallback(() => setOpenId(null), []);

  useEffect(() => {
    if (!openId) return;
    const onDown = (e: MouseEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      // A modal dialog (the owner's popup) owns Escape while it is open.
      if (e.key !== 'Escape' || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      // Focus first, then close, so keyboard users land back on the button they opened.
      buttons.current[openId]?.focus();
      setOpenId(null);
    };
    const onFocus = (e: FocusEvent) => {
      // Focus landing on the page itself (switching back to the window) is not leaving the menu.
      const t = e.target;
      if (!(t instanceof Element) || t === document.body) return;
      if (!headerRef.current?.contains(t)) close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocus);
    };
  }, [openId, close]);

  const hoverOpen = (id: string) => (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpenId(id), openId ? 0 : 90);
  };
  const hoverLeave = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpenId(null), 160);
  };

  const menuActive = (m: MegaMenu) => m.columns.some((c) => c.links.some((l) => l.href === pathname)) || m.titleHref === pathname;
  const solid = scrolled || openId !== null;

  return (
    <header
      ref={headerRef}
      onPointerLeave={hoverLeave}
      onPointerEnter={() => clearTimeout(hoverTimer.current)}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-200 ${solid ? 'border-b border-line bg-ink-900/95 backdrop-blur-xl' : 'border-b border-transparent'}`}
    >
      <div className="container-page flex h-[4.5rem] items-center justify-between gap-4">
        <Wordmark />

        <nav className="hidden h-full items-center gap-1 lg:flex" aria-label="Main">
          {TOP_NAV.map((item) => {
            if (item.kind === 'link') {
              const active = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onPointerEnter={hoverLeave}
                  aria-current={active ? 'page' : undefined}
                  className={`press relative flex h-full items-center px-3.5 text-[0.92rem] transition-colors ${active ? 'text-mist-100' : 'text-mist-400 hover:text-mist-100'}`}
                >
                  {item.label}
                  {active && <span aria-hidden="true" className="absolute inset-x-3.5 bottom-3 h-0.5 rounded-full bg-spark-500" />}
                </Link>
              );
            }
            const m = item.menu;
            const open = openId === m.id;
            const active = menuActive(m);
            return (
              <div key={m.id} className="flex h-full items-center" onPointerEnter={hoverOpen(m.id)}>
                <button
                  ref={(el) => {
                    buttons.current[m.id] = el;
                  }}
                  type="button"
                  aria-expanded={open}
                  aria-controls={`mega-${m.id}`}
                  onClick={() => setOpenId(open ? null : m.id)}
                  className={`press relative flex h-full items-center gap-1.5 px-3.5 text-[0.92rem] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-6px] focus-visible:outline-spark-500 ${
                    open || active ? 'text-mist-100' : 'text-mist-400 hover:text-mist-100'
                  }`}
                >
                  {m.label}
                  <Chevron open={open} />
                  {(open || active) && <span aria-hidden="true" className="absolute inset-x-3.5 bottom-3 h-0.5 rounded-full bg-spark-500" />}
                </button>
                <Panel menu={m} id={`mega-${m.id}`} pathname={pathname} hidden={!open} />
              </div>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <EmailLink
            email={salesEmail}
            className="press inline-flex items-center gap-2 rounded-full border border-line-2 bg-ink-900 py-2.5 pl-3.5 pr-4 text-[0.9rem] font-medium text-mist-300 transition-colors hover:border-mist-500 hover:text-mist-100"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
            {salesEmail}
          </EmailLink>
          <Link href="/contact#audit" onClick={() => trackCta('header-book', pathname, '/contact')} className="btn btn-signal px-5 py-2.5 text-[0.9rem]">
            Book a meeting
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <Link href="/contact#audit" className="btn btn-signal px-4 py-2.5 text-[0.85rem]">
            Book a meeting
          </Link>
          <button
            type="button"
            onClick={() => setSheet((v) => !v)}
            className="btn btn-ghost h-11 w-11 p-0"
            aria-expanded={sheet}
            aria-controls="mobile-nav"
            aria-label={sheet ? 'Close menu' : 'Open menu'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {sheet ? (
                <>
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </>
              ) : (
                <>
                  <path d="M3 7h18" />
                  <path d="M3 12h18" />
                  <path d="M3 17h18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>


      <div id="mobile-nav" hidden={!sheet} className="h-[calc(100dvh-4.5rem)] overflow-y-auto border-t border-line bg-ink-900 lg:hidden">
        {/* Phones: big words, then icon tiles with labels only. One section open
            at a time (the shared `name`), no descriptions, nothing to read. */}
        <div className="container-page flex min-h-full flex-col py-4">
          <div className="grid gap-1">
            {TOP_NAV.map((item) =>
              item.kind === 'link' ? (
                <Link key={item.href} href={item.href} className="press flex min-h-[3.5rem] items-center border-b border-line px-1 font-display text-2xl font-bold tracking-[-0.03em] text-mist-100">
                  {item.label}
                </Link>
              ) : (
                <details key={item.menu.id} name="mobile-nav" className="group border-b border-line">
                  <summary className="press flex min-h-[3.5rem] cursor-pointer list-none items-center justify-between px-1 font-display text-2xl font-bold tracking-[-0.03em] text-mist-100 [&::-webkit-details-marker]:hidden">
                    {item.menu.label}
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-veil-2 transition-transform duration-200 group-open:rotate-180 group-open:bg-signal group-open:text-on-signal">
                      <Chevron open={false} />
                    </span>
                  </summary>
                  <ul className="step-in-fwd grid grid-cols-2 gap-2 pb-4 pt-1">
                    {item.menu.quick.map((l) => (
                      <li key={l.href + l.label}>
                        <Link
                          href={l.href}
                          aria-current={pathname === l.href ? 'page' : undefined}
                          className="press flex h-full min-h-[4.25rem] flex-col justify-center gap-1.5 rounded-xl border border-line-2 bg-ink-950 px-3 py-2.5 active:bg-[#09090b] active:text-white"
                        >
                          <NavIcon name={l.icon} className="h-5 w-5 text-spark-600" />
                          <span className="text-[0.88rem] font-semibold leading-tight text-mist-100">{l.label}</span>
                          {l.note && <span className="text-[0.72rem] font-semibold text-spark-600">{l.note}</span>}
                        </Link>
                      </li>
                    ))}
                    <li className="col-span-2">
                      <Link href={item.menu.titleHref} className="press flex min-h-[2.75rem] items-center gap-2 px-1 text-sm font-semibold text-spark-600">
                        All of {item.menu.label.toLowerCase()} →
                      </Link>
                    </li>
                  </ul>
                </details>
              ),
            )}
          </div>

          <div className="mt-auto grid gap-2 pt-6">
            <Link href="/contact#audit" className="btn btn-signal px-5 py-4 text-base">
              Book a meeting
            </Link>
            <EmailLink email={salesEmail} align="left" className="flex min-h-[3rem] items-center justify-center gap-2.5 rounded-xl text-sm text-mist-400 hover:text-mist-100">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
              {salesEmail}
            </EmailLink>
          </div>
        </div>
      </div>
    </header>
  );
}
